import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	output,
} from '@angular/core';
import { IconsModule } from '@ui/icons/icons.module';
import { AVATAR_VARIANT } from '@ui/utils/constants';

@Component({
	selector: 'app-block-button',
	standalone: true,
	imports: [IconsModule, CommonModule],
	templateUrl: './block-button.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { class: 'w-full h-full' },
})
export class BlockButtonComponent {
	private BLOCK_BUTTON_COLOR_VARIANT_ = computed(() => ({
		[AVATAR_VARIANT.Emerald]: `${!this.disabled() ? 'hover:bg-emerald-100 active:border-emerald-400' : ''} ${this.isSelected() ? 'bg-emerald-100' : 'bg-emerald-50'} border-emerald-200 ring-emerald-400`,
		[AVATAR_VARIANT.Lime]: `${!this.disabled() ? 'hover:bg-lime-100 active:border-lime-400' : ''} ${this.isSelected() ? 'bg-lime-100' : 'bg-lime-50'} border-lime-200 ring-lime-400`,
		[AVATAR_VARIANT.Indigo]: `${!this.disabled() ? 'hover:bg-indigo-100 active:border-indigo-400' : ''} ${this.isSelected() ? 'bg-indigo-100' : 'bg-indigo-50'} border-indigo-200 ring-indigo-400`,
		[AVATAR_VARIANT.Orange]: `${!this.disabled() ? 'hover:bg-orange-100 active:border-orange-400' : ''} ${this.isSelected() ? 'bg-orange-100' : 'bg-orange-50'} border-orange-200 ring-orange-400`,
		[AVATAR_VARIANT.Purple]: `${!this.disabled() ? 'hover:bg-purple-100 active:border-purple-400' : ''} ${this.isSelected() ? 'bg-purple-100' : 'bg-purple-50'} border-purple-200 ring-purple-400`,
		[AVATAR_VARIANT.Pink]: `${!this.disabled() ? 'hover:bg-pink-100 active:border-pink-400' : ''} ${this.isSelected() ? 'bg-pink-100' : 'bg-pink-50'} border-pink-200 ring-pink-400`,
	}));
	ariaLabel = input<string>(null);
	label = input<string>(null);
	iconName = input<string>(null);
	variant = input<AVATAR_VARIANT>(AVATAR_VARIANT.Emerald);
	disabled = input<boolean>(false);
	isSelected = input<boolean>(false);
	size = input<'sm' | 'md' | 'lg'>('sm');
	clicked = output();
	dragstart = output<DragEvent>();
	dragend = output<DragEvent>();
	classes = computed(() => [
		this.getColorVariant(),
		this.getSizeVariant(),
		this.getDragVariant(),
		this.getMinWidthVariant(),
		this.getSelectedVariant(),
	]);
	iconClasses = computed(() => this.getIconSizeVariant());
	draggable = input<boolean>(false);

	private getDragVariant = computed(() => {
		return this.draggable() ? 'cursor-grab active:cursor-grabbing' : '';
	});

	private getSelectedVariant = computed(() => {
		return this.isSelected() ? 'ring-2' : '';
	});

	private getSizeVariant = computed(() => {
		return (
			this.iconName()
				? {
						sm: 'pl-2.5 pr-3 text-xs',
						md: 'pl-3 pr-4 text-sm',
						lg: 'pl-3 pr-4 text-base',
					}
				: {
						sm: 'p-3 text-xs',
						md: 'p-4 text-sm',
						lg: 'p-4 text-base',
					}
		)[this.size()];
	});

	private getColorVariant = computed(() => {
		return this.BLOCK_BUTTON_COLOR_VARIANT_()[
			this.variant() || AVATAR_VARIANT.Emerald
		];
	});

	private getMinWidthVariant = computed(() => {
		return this.iconName()
			? {
					sm: 'min-w-9',
					md: 'min-w-10',
					lg: 'min-w-10',
				}[this.size()]
			: '';
	});

	private getIconSizeVariant = computed(() => {
		return {
			sm: 'min-w-3.5 min-h-3.5 h-3.5 w-3.5',
			md: 'min-w-4 min-h-4 h-4 w-4',
			lg: 'min-w-4 min-h-4 h-4 w-4',
		}[this.size()];
	});
}
